import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Table } from "react-bootstrap";
import { Building2, Check, Copy, KeyRound, Plus, RefreshCw, Rocket, RotateCw } from "lucide-react";
import {
  fetchOrganizations,
  fetchPartnerKeys,
  issuePartnerKey,
  revokePartnerKey,
} from "../../utilities/AdminUtilities";
import { EMBED_SCREENS } from "../../Embed/embedRegistry";
import LoadingComponent from "../Common/LoadingComponent";
import { FriendlyDate } from "./AdminUi";

const screenTitle = (key) => EMBED_SCREENS[key]?.title || key;

// What a key can open comes from the organisation's EMBEDDED account plans
// (Account tab), the same rules as the app sidebar; the API returns `screens`.
const ScreenBadges = ({ screens }) =>
  screens?.length ? (
    <div className="d-flex flex-wrap gap-1">
      {screens.map((screen) => (
        <span key={screen} className="pk-chip">
          {screenTitle(screen)}
        </span>
      ))}
    </div>
  ) : (
    <span className="pk-chip pk-chip-warn" title="Add plans to the account in the Account tab">
      No plans
    </span>
  );

// The API returns naive UTC timestamps; mark them UTC before display.
const asUtc = (value) =>
  value && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(value) ? `${value}Z` : value;

const keyStatus = (key) => {
  if (key.revoked_at) return { label: "Revoked", tone: "off" };
  if (key.expires_at && new Date(asUtc(key.expires_at)) < new Date()) {
    return { label: "Expired", tone: "warn" };
  }
  return { label: "Active", tone: "on" };
};

const SOON_MS = 30 * 24 * 3600 * 1000;
const expiresSoon = (key) =>
  Boolean(key.expires_at) && new Date(asUtc(key.expires_at)) - Date.now() < SOON_MS;

const Stat = ({ label, value, tone }) => (
  <div className={`pk-stat${tone ? ` pk-stat-${tone}` : ""}`}>
    <div className="pk-stat-value">{value}</div>
    <div className="pk-stat-label">{label}</div>
  </div>
);

function PartnerKeyManagement() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [issueFor, setIssueFor] = useState(null); // null | {} (new) | key row (rotate)
  const [revealedKey, setRevealedKey] = useState(null);
  const [revoking, setRevoking] = useState(null);

  const loadKeys = useCallback(async () => {
    setLoading(true);
    try {
      setKeys((await fetchPartnerKeys()) || []);
    } catch (error) {
      setFeedback({ variant: "danger", message: `Could not load keys: ${error.message}` });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const handleIssued = (result) => {
    setIssueFor(null);
    setRevealedKey(result);
    loadKeys();
  };

  const handleRevoke = async () => {
    try {
      await revokePartnerKey(revoking.organisation_id);
      setFeedback({
        variant: "success",
        message: `Revoked the key for ${revoking.organisation_name}. New launches are blocked; open sessions continue until they expire.`,
      });
      loadKeys();
    } catch (error) {
      setFeedback({ variant: "danger", message: `Revoke failed: ${error.message}` });
    } finally {
      setRevoking(null);
    }
  };

  const counts = useMemo(() => {
    const tally = { Active: 0, Expiring: 0, Inactive: 0 };
    keys.forEach((key) => {
      const status = keyStatus(key);
      if (status.label === "Active") {
        tally.Active += 1;
        if (expiresSoon(key)) tally.Expiring += 1;
      } else {
        tally.Inactive += 1;
      }
    });
    return tally;
  }, [keys]);

  return (
    <div className="pk-wrap">
      <div className="pk-head">
        <div className="pk-head-text">
          <h2 className="pk-title">Partner API keys</h2>
          <p className="pk-sub">
            Let partner organisations open EMAlpha screens inside their own product.
          </p>
        </div>
        <div className="pk-head-actions">
          <Button
            variant="outline-secondary"
            className="pk-icon-btn"
            onClick={loadKeys}
            disabled={loading}
            title="Refresh"
            aria-label="Refresh"
          >
            <RefreshCw size={16} className={loading ? "pk-spin" : undefined} />
          </Button>
          <Button variant="primary" className="pk-primary" onClick={() => setIssueFor({})}>
            <Plus size={16} />
            Issue key
          </Button>
        </div>
      </div>

      <ol className="pk-steps">
        <li>
          <span className="pk-step-icon"><Building2 size={16} /></span>
          <div>
            <div className="pk-step-title">Partner embed account</div>
            <div className="pk-step-text">
              The organisation needs an account with integration type <strong>Partner embed</strong>.
            </div>
          </div>
        </li>
        <li>
          <span className="pk-step-icon"><KeyRound size={16} /></span>
          <div>
            <div className="pk-step-title">One key per organisation</div>
            <div className="pk-step-text">
              Issue it here and share it securely. Rotating replaces the old key instantly.
            </div>
          </div>
        </li>
        <li>
          <span className="pk-step-icon"><Rocket size={16} /></span>
          <div>
            <div className="pk-step-title">
              Partner calls <code>POST /partner/launch</code>
            </div>
            <div className="pk-step-text">
              Screens are whatever the account&apos;s plans unlock: manage them in the Account tab.
            </div>
          </div>
        </li>
      </ol>

      {feedback && (
        <Alert
          variant={feedback.variant}
          dismissible
          onClose={() => setFeedback(null)}
          className="mb-0"
        >
          {feedback.message}
        </Alert>
      )}

      {loading && !keys.length ? (
        <LoadingComponent />
      ) : keys.length ? (
        <>
          <div className="pk-stats">
            <Stat label="Keys issued" value={keys.length} />
            <Stat label="Active" value={counts.Active} tone="positive" />
            <Stat label="Expiring in 30 days" value={counts.Expiring} tone="neutral" />
            <Stat label="Revoked or expired" value={counts.Inactive} />
          </div>

          <div className="up-card">
            <Table hover responsive className="up-table pk-table">
              <thead>
                <tr>
                  <th>Organisation</th>
                  <th>Key</th>
                  <th>Screens</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Expires</th>
                  <th>Last used</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {keys.map((key) => {
                  const status = keyStatus(key);
                  return (
                    <tr
                      key={key.organisation_id}
                      className={status.label === "Active" ? undefined : "pk-row-off"}
                    >
                      <td>
                        <div className="up-person">
                          <span className="up-avatar">
                            {(key.organisation_name || "?")[0].toUpperCase()}
                          </span>
                          <span className="up-name">{key.organisation_name}</span>
                        </div>
                      </td>
                      <td>
                        <span className="pk-key">emk_{key.key_prefix}_••••</span>
                      </td>
                      <td>
                        <ScreenBadges screens={key.screens} />
                      </td>
                      <td>
                        <span className={`pk-status pk-status-${status.tone}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="up-date"><FriendlyDate value={asUtc(key.created_at)} /></td>
                      <td className="up-date">
                        {key.expires_at ? <FriendlyDate value={asUtc(key.expires_at)} /> : "Never"}
                      </td>
                      <td className="up-date">
                        {key.last_used_at ? (
                          <FriendlyDate value={asUtc(key.last_used_at)} />
                        ) : (
                          <span className="pk-never">Never used</span>
                        )}
                      </td>
                      <td className="text-nowrap text-end">
                        <div className="pk-actions">
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => setIssueFor(key)}
                          >
                            <RotateCw size={14} />
                            Rotate
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            disabled={Boolean(key.revoked_at)}
                            onClick={() => setRevoking(key)}
                          >
                            Revoke
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </>
      ) : (
        <div className="pk-empty">
          <span className="pk-empty-icon"><KeyRound size={22} /></span>
          <div className="pk-empty-title">No partner keys yet</div>
          <p className="pk-empty-text">
            Issue a key to let a partner organisation launch embedded screens. You&apos;ll see it
            only once, so have a secure way to share it ready.
          </p>
          <Button variant="primary" className="pk-primary" onClick={() => setIssueFor({})}>
            <Plus size={16} />
            Issue first key
          </Button>
        </div>
      )}

      {issueFor && (
        <IssueKeyModal
          existing={issueFor.organisation_id ? issueFor : null}
          keyedOrgIds={keys.map((k) => k.organisation_id)}
          onHide={() => setIssueFor(null)}
          onIssued={handleIssued}
        />
      )}

      <RevealKeyModal result={revealedKey} onHide={() => setRevealedKey(null)} />

      <Modal show={Boolean(revoking)} onHide={() => setRevoking(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Revoke key?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <strong>{revoking?.organisation_name}</strong> will immediately be unable to launch
          screens. Windows already open keep working until their session expires. To restore
          access, issue a new key.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setRevoking(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleRevoke}>
            Revoke
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default PartnerKeyManagement;

// Issue a new key, or rotate `existing`.
const IssueKeyModal = ({ existing, keyedOrgIds, onHide, onIssued }) => {
  const [organisations, setOrganisations] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [organisationId, setOrganisationId] = useState(existing?.organisation_id || "");
  const [expiresOn, setExpiresOn] = useState(
    existing?.expires_at ? asUtc(existing.expires_at).slice(0, 10) : "",
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!existing) {
      fetchOrganizations({ setOrganizations: setOrganisations, name: "", setLoading: setOrgsLoading });
    }
  }, [existing]);

  const replacesKey = Boolean(existing) || keyedOrgIds.includes(organisationId);
  const sortedOrganisations = useMemo(
    () =>
      [...(organisations || [])].sort((a, b) =>
        (a.organisation_name || "").localeCompare(b.organisation_name || ""),
      ),
    [organisations],
  );

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const result = await issuePartnerKey({
        organisationId,
        // End of the chosen day, UTC (the API compares naive UTC times).
        expiresAt: expiresOn ? `${expiresOn}T23:59:59` : null,
      });
      onIssued(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show onHide={onHide} centered size="lg">
      <Modal.Header closeButton className="pv-header">
        <div className="w-100">
          <div className="pv-eyebrow">{existing ? "Rotate Partner Key" : "Issue Partner Key"}</div>
          <div className="pv-title up-title text-break">
            {existing?.organisation_name ||
              sortedOrganisations.find((o) => String(o.id) === organisationId)?.organisation_name ||
              "Choose an organisation"}
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form>
          {!existing && (
            <Form.Group className="ep-group mb-3">
              <Form.Label className="pv-label">Organisation</Form.Label>
              <Form.Select
                value={organisationId}
                disabled={orgsLoading}
                onChange={(e) => setOrganisationId(e.target.value)}
              >
                <option value="">{orgsLoading ? "Loading…" : "Select an organisation"}</option>
                {sortedOrganisations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.organisation_name}
                    {keyedOrgIds.includes(String(org.id)) ? " (has a key)" : ""}
                  </option>
                ))}
              </Form.Select>
              <Form.Text muted>
                Must have an account with integration type "Partner embed" (set when creating the
                organisation).
              </Form.Text>
            </Form.Group>
          )}

          {existing && (
            <Form.Group className="ep-group mb-3">
              <Form.Label className="pv-label">Screens</Form.Label>
              <ScreenBadges screens={existing.screens} />
              <Form.Text muted>From the account's plans; change them in the Account tab.</Form.Text>
            </Form.Group>
          )}

          <Form.Group className="ep-group">
            <Form.Label className="pv-label">Expires on (optional)</Form.Label>
            <Form.Control
              type="date"
              value={expiresOn}
              min={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setExpiresOn(e.target.value)}
            />
            <Form.Text muted>End of that day, UTC. Leave empty for no expiry.</Form.Text>
          </Form.Group>
        </Form>

        {replacesKey && (
          <Alert variant="warning" className="mt-3 mb-0">
            This organisation already has a key. Issuing replaces it: the current key stops
            working immediately, so the partner must switch to the new one.
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer className="ep-footer">
        <Button variant="outline-secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={submitting || !organisationId}
          onClick={submit}
        >
          {submitting ? "Issuing…" : replacesKey ? "Rotate Key" : "Issue Key"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Shows the plaintext key once. The API never returns it again.
const RevealKeyModal = ({ result, onHide }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => setCopied(false), [result]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.api_key);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Modal show={Boolean(result)} onHide={onHide} centered backdrop="static" keyboard={false}>
      <Modal.Header className="pv-header">
        <div className="w-100">
          <div className="pv-eyebrow">New partner key</div>
          <div className="pv-title up-title text-break">{result?.organisation_name}</div>
        </div>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="warning">
          Copy this key now. It is shown only once and can't be retrieved later; if it's lost,
          rotate the key. Share it with the partner through a secret-sharing tool, never by email
          or chat.
        </Alert>
        <div className="pk-secret">
          <input
            readOnly
            className="pk-secret-input"
            value={result?.api_key || ""}
            onFocus={(e) => e.target.select()}
            aria-label="API key"
          />
          <Button variant={copied ? "success" : "primary"} className="pk-primary" onClick={copy}>
            {copied ? <Check size={15} /> : <Copy size={15} />}
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>
        <div className="pk-secret-screens">
          <span className="pv-label mb-0">Screens</span>
          <ScreenBadges screens={result?.screens} />
        </div>
      </Modal.Body>
      <Modal.Footer className="ep-footer">
        <Button variant="outline-secondary" onClick={onHide}>
          I&apos;ve saved it
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
